import React, { useEffect } from "react";
import { Route, Routes, useSearchParams } from "react-router";
import { registerUserToSlack } from "api/user";
import { LoadingUserInfoPage } from "components/App/components/LoginWall/components/LoadingUserInfoPage";
import { LoginPromptPage } from "components/App/components/LoginWall/components/LoginPromptPage";
import { useCompanyContext } from "context/companyContext";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { useOpenGlobalErrorModal } from "hooks/useGlobalError";
import ApiError from "utils/errors/apiError";
import { type TErrorId } from "utils/errors/mapApiErrorParams";

export const LoginWall: FC = ({ children }) => {
	const { user, isLoggedIn, reloadUser, loginDetails, loading } = useAuthenticatedUser();
	const {
		state: { company },
		actions: { loadCompany }
	} = useCompanyContext();
	const openGlobalErrorModal = useOpenGlobalErrorModal();
	const [searchParams] = useSearchParams();

	useEffect(() => {
		const listener = (e: Event) => {
			const customEvent = e as CustomEvent;
			const searchParams = customEvent.detail as URLSearchParams;
			const provider = searchParams.get("provider") as "okta" | "microsoft" | "google" | undefined;
			const method = searchParams.get("method") as "saml" | "oidc" | undefined;
			void reloadUser(provider && method ? { method, provider } : undefined);
		};
		addEventListener("loginSuccess", listener);
		return () => removeEventListener("loginSuccess", listener);
	}, [loginDetails, reloadUser]);

	useEffect(() => {
		if (isLoggedIn && company?.id && user?.email) {
			const slackUserId = searchParams.get("slackUserId");
			const companyId = searchParams.get("companyId");
			const userEmail = searchParams.get("userEmail");
			if (slackUserId) {
				if (companyId === company?.id && userEmail === user?.email) {
					void registerUserToSlack(slackUserId);
				}
			}
		}
	}, [searchParams, company?.id, user?.email, isLoggedIn]);

	useEffect(() => {
		const listener = (e: Event) => {
			const customEvent = e as CustomEvent;
			const searchParams = customEvent.detail as URLSearchParams;
			const errorIdBase64 = searchParams.get("errorId");
			let errorId: TErrorId = "request.internalServerError";
			if (errorIdBase64) errorId = Buffer.from(errorIdBase64, "base64").toString("utf-8") as TErrorId;
			openGlobalErrorModal(new ApiError({ errorId }));
		};
		addEventListener("loginFailure", listener);
		return () => removeEventListener("loginFailure", listener);
	}, [openGlobalErrorModal]);

	useEffect(() => {
		if (user) {
			void loadCompany();
		}
	}, [loadCompany, user]);

	const fetchingUserInfo = isLoggedIn && (!user || !company);

	if (loading || fetchingUserInfo) {
		return <LoadingUserInfoPage />;
	}

	if (!isLoggedIn) {
		return (
			<Routes>
				<Route path="/">
					<Route index element={<LoginPromptPage />} />
					<Route path="*" element={<LoginPromptPage />} />
				</Route>
			</Routes>
		);
	}

	return <>{children}</>;
};
